import { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang, getLocale } from "../lang/useLanguage";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR")} ₺` : "—";

const getKaizenStatus = () => {
  const l = getLang(); const k = l.kaizenPage || {};
  return {
    idea:         { label: k.statusIdea,       color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
    reviewing:    { label: k.statusReviewing, color: "#fbbf24", bg: "rgba(251,191,36,0.12)"  },
    approved:     { label: k.statusApproved,   color: "#60a5fa", bg: "rgba(96,165,250,0.12)"  },
    implementing: { label: k.statusImplementing, color: "#a78bfa", bg: "rgba(167,139,250,0.12)" },
    completed:    { label: k.statusCompleted,  color: "#00ff9d", bg: "rgba(0,255,157,0.12)"   },
    rejected:     { label: k.statusRejected,  color: "#f87171", bg: "rgba(248,113,113,0.12)" },
  };
};

const LIGHT_KAIZEN = {
  idea:{ color:"#64748b",bg:"#f8fafc" }, reviewing:{ color:"#d97706",bg:"#fffbeb" },
  approved:{ color:"#2563eb",bg:"#eff6ff" }, implementing:{ color:"#7c3aed",bg:"#f5f3ff" },
  completed:{ color:"#059669",bg:"#ecfdf5" }, rejected:{ color:"#dc2626",bg:"#fef2f2" },
};

const getKaizenCategory = () => {
  const l = getLang(); const k = l.kaizenPage || {};
  return {
    safety:      { label: k.catSafety, icon: "🦺" },
    quality:     { label: k.catQuality,        icon: "✅" },
    efficiency:  { label: k.catEfficiency,    icon: "⚡" },
    cost:        { label: k.catCost,       icon: "💰" },
    environment: { label: k.catEnvironment,         icon: "🌿" },
    ergonomics:  { label: k.catErgonomics,      icon: "🪑" },
    maintenance: { label: k.catMaintenance,         icon: "🔧" },
    other:       { label: k.catOther,         icon: "📋" },
  };
};

const getFivesCategories = () => {
  const l = getLang(); const k = l.kaizenPage || {};
  return [
    { key:"seiri",    label:k.seiriLabel,    icon:"🗂️", desc:k.seiriDesc },
    { key:"seiton",   label:k.seitonLabel,  icon:"📐", desc:k.seitonDesc },
    { key:"seiso",    label:k.seisoLabel,    icon:"🧹", desc:k.seisoDesc },
    { key:"seiketsu", label:k.seiketsuLabel, icon:"📋", desc:k.seiketsuDesc },
    { key:"shitsuke", label:k.shitsukeLabel, icon:"🎯", desc:k.shitsukeDesc },
  ];
};

const scKaizen = (s, tn) => tn === "light" ? (LIGHT_KAIZEN[s] || LIGHT_KAIZEN.idea) : (getKaizenStatus()[s] || getKaizenStatus().idea);

// ─── BADGE ────────────────────────────────────────────────────────────────
const KaizenBadge = ({ status, themeName }) => {
  const c = scKaizen(status, themeName);
  const cfg = getKaizenStatus()[status] || getKaizenStatus().idea;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:c.bg, color:c.color, border:`1px solid ${c.color}44`, letterSpacing:"0.04em", textTransform:"uppercase", whiteSpace:"nowrap" }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:c.color }} />
      {cfg.label}
    </span>
  );
};

// ─── KAİZEN DETAY MODAL ───────────────────────────────────────────────────
const KaizenDetailModal = ({ record, onClose, onEdit, onStatusChange, t, themeName }) => {
  const { t: l } = useLanguage();
  const k = l.kaizenPage || {};
  const KAIZEN_STATUS   = getKaizenStatus();
  const KAIZEN_CATEGORY = getKaizenCategory();
  const [loading, setLoading] = useState(false);
  const nextStatus = { idea:"reviewing", reviewing:"approved", approved:"implementing", implementing:"completed" };
  const next   = nextStatus[record.status];
  const catCfg = KAIZEN_CATEGORY[record.category] || KAIZEN_CATEGORY.other;

  const handleStatus = async (s) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/kaizen/${record.id}/status`, { method:"PATCH", headers:headers(), body:JSON.stringify({ status:s }) });
      if (res.ok) onStatusChange();
    } catch {}
    setLoading(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:680, maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"20px 28px", borderBottom:`1px solid ${t.border.default}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:t.bg.header }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>{catCfg.icon}</div>
            <div>
              <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>{record.title}</div>
              <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{catCfg.label} · {record.submitter_name||record.created_by||"—"}</div>
            </div>
            <KaizenBadge status={record.status} themeName={themeName} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {record.status === "approved" && (
              <button onClick={() => {
                const params = new URLSearchParams({ from_kaizen:record.id, product_name:record.title||"", notes:`Kaizen #${record.id}: ${record.suggestion||""}`, machine_id:record.machine_id||"", priority:record.priority||"normal" });
                window.dispatchEvent(new CustomEvent("openWorkOrder", { detail:Object.fromEntries(params) }));
                onClose();
              }} style={{ padding:"8px 14px", borderRadius:8, border:"none", cursor:"pointer", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff", fontSize:12, fontWeight:700, boxShadow:"0 4px 12px rgba(16,185,129,0.3)" }}>
                📋 {k.createWorkOrder}
              </button>
            )}
            {record.status !== "completed" && record.status !== "rejected" && (
              <button onClick={() => handleStatus("completed")} disabled={loading} style={{ padding:"8px 14px", borderRadius:8, border:"none", cursor:"pointer", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff", fontSize:12, fontWeight:700, opacity:loading?0.6:1, boxShadow:"0 4px 12px rgba(16,185,129,0.3)" }}>
                ✅ {k.statusCompleted}
              </button>
            )}
            {next && (
              <button onClick={() => handleStatus(next)} disabled={loading} style={{ padding:"8px 14px", borderRadius:8, border:"none", cursor:"pointer", background:scKaizen(next,themeName).color, color:"#fff", fontSize:12, fontWeight:700, opacity:loading?0.6:1 }}>
                → {KAIZEN_STATUS[next]?.label}
              </button>
            )}
            {record.status !== "rejected" && record.status !== "completed" && (
              <button onClick={() => handleStatus("rejected")} disabled={loading} style={{ padding:"8px 12px", borderRadius:8, border:`1px solid ${t.accent.red}44`, background:t.accent.redBg, color:t.accent.red, cursor:"pointer", fontSize:12 }}>
                {k.reject}
              </button>
            )}
            <button onClick={() => onEdit(record)} style={{ padding:"8px 12px", borderRadius:8, border:`1px solid ${t.accent.blueBorder}`, background:t.accent.blueBg, color:t.accent.blue, cursor:"pointer", fontSize:12 }}>✏️</button>
            <button onClick={onClose} style={{ padding:"8px 12px", borderRadius:8, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:12 }}>✕</button>
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:28, display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:t.text.label, marginBottom:14, textTransform:"uppercase", letterSpacing:"0.06em" }}>📋 {k.kaizenInfo}</div>
              {[
                [k.category_,        catCfg.label],
                [k.priority_,         record.priority],
                [k.submitter,        record.submitter_name||record.created_by],
                [k.department_,       record.department],
                [k.machineArea,     record.machine_id||record.area],
                [k.submitDate, fmt(record.created_at)],
                [k.targetDate_,fmt(record.target_date)],
                [k.estimatedSaving_, record.estimated_saving?`${record.estimated_saving.toLocaleString("tr-TR")} ₺`:"—"],
              ].map(([lbl,val]) => (
                <div key={lbl} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${t.border.subtle}`, fontSize:13 }}>
                  <span style={{ color:t.text.label }}>{lbl}</span>
                  <span style={{ color:t.text.primary, fontWeight:500 }}>{val||"—"}</span>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.text.label, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.06em" }}>🔍 {k.problem_}</div>
                <div style={{ fontSize:13, color:t.text.secondary, lineHeight:1.7 }}>{record.problem||"—"}</div>
              </div>
              <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:t.text.label, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.06em" }}>💡 {k.suggestion_}</div>
                <div style={{ fontSize:13, color:t.text.secondary, lineHeight:1.7 }}>{record.suggestion||"—"}</div>
              </div>
              {record.result && (
                <div style={{ background:t.accent.greenBg, border:`1px solid ${t.accent.green}44`, borderRadius:14, padding:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:t.accent.green, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.06em" }}>✅ {k.result_}</div>
                  <div style={{ fontSize:13, color:t.text.secondary, lineHeight:1.7 }}>{record.result}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── 5S DENETİM FORMU ────────────────────────────────────────────────────
const FiveSAuditModal = ({ audit, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const k = l.kaizenPage || {};
  const FIVES_CATEGORIES = getFivesCategories();
  const isEdit = !!audit;
  const [form, setForm] = useState({ area:audit?.area||"", auditor:audit?.auditor||"", audit_date:audit?.audit_date?.slice(0,10)||new Date().toISOString().slice(0,10), seiri:audit?.seiri||0, seiton:audit?.seiton||0, seiso:audit?.seiso||0, seiketsu:audit?.seiketsu||0, shitsuke:audit?.shitsuke||0, notes:audit?.notes||"", photos:audit?.photos||"" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = (fk, v) => setForm(p => ({ ...p, [fk]:v }));
  const totalScore = Number(form.seiri)+Number(form.seiton)+Number(form.seiso)+Number(form.seiketsu)+Number(form.shitsuke);
  const scoreColor = totalScore>=40?t.accent.green:totalScore>=25?t.accent.yellow:t.accent.red;

  const handleSave = async () => {
    if (!form.area||!form.auditor) { setError(k.errAreaAuditor); return; }
    setLoading(true); setError("");
    try {
      const url = isEdit ? `${API}/fives/${audit.id}` : `${API}/fives`;
      const res = await fetch(url, { method:isEdit?"PUT":"POST", headers:headers(), body:JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail||(k.errGeneric));
      onSaved();
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const IS = { width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:640, maxHeight:"92vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"20px 28px", borderBottom:`1px solid ${t.border.default}`, display:"flex", justifyContent:"space-between", alignItems:"center", background:t.bg.header }}>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>
              {isEdit?`✏️ ${k.editFivesAudit}`:`📋 ${k.newFivesAudit}`}
            </div>
            <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{k.fivesScoreHint}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:t.text.muted, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:28 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:20 }}>
            {[
              { label:`${k.auditArea} *`, fk:"area",       placeholder:k.auditAreaPh },
              { label:`${k.auditor_} *`,         fk:"auditor",    placeholder:k.auditorPh },
              { label:k.auditDate,         fk:"audit_date", type:"date" },
            ].map(f=>(
              <div key={f.fk}>
                <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{f.label}</label>
                <input type={f.type||"text"} value={form[f.fk]} placeholder={f.placeholder} onChange={e=>set(f.fk,e.target.value)} style={IS} />
              </div>
            ))}
          </div>
          <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:14, padding:"16px 20px", marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:14, fontWeight:600, color:t.text.primary }}>{k.totalScore}</span>
            <div><span style={{ fontSize:32, fontWeight:800, color:scoreColor }}>{totalScore}</span><span style={{ fontSize:14, color:t.text.muted }}> / 50</span></div>
          </div>
          {FIVES_CATEGORIES.map(cat=>(
            <div key={cat.key} style={{ marginBottom:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text.primary }}>{cat.icon} {cat.label}</div>
                  <div style={{ fontSize:12, color:t.text.muted }}>{cat.desc}</div>
                </div>
                <div style={{ fontSize:22, fontWeight:800, color:form[cat.key]>=8?t.accent.green:form[cat.key]>=5?t.accent.yellow:t.accent.red }}>{form[cat.key]}</div>
              </div>
              <div style={{ display:"flex", gap:4 }}>
                {[0,1,2,3,4,5,6,7,8,9,10].map(n=>(
                  <button key={n} onClick={()=>set(cat.key,n)} style={{ flex:1, padding:"8px 0", borderRadius:6, cursor:"pointer", border:`1px solid ${form[cat.key]===n?(n>=8?t.accent.green:n>=5?t.accent.yellow:t.accent.red):t.border.input}`, background:form[cat.key]===n?(n>=8?t.accent.greenBg:n>=5?t.accent.yellowBg:t.accent.redBg):t.bg.input, color:form[cat.key]===n?(n>=8?t.accent.green:n>=5?t.accent.yellow:t.accent.red):t.text.muted, fontSize:12, fontWeight:700, transition:"all 0.1s" }}>{n}</button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{k.notes}</label>
            <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} rows={3} style={{ ...IS, resize:"vertical" }} />
          </div>
          {error && <div style={{ marginTop:14, padding:"10px 16px", background:t.accent.redBg, border:`1px solid ${t.accent.red}44`, borderRadius:8, color:t.accent.red, fontSize:13 }}>{error}</div>}
        </div>
        <div style={{ padding:"16px 28px", borderTop:`1px solid ${t.border.default}`, display:"flex", justifyContent:"flex-end", gap:12, background:t.bg.header }}>
          <button onClick={onClose} style={{ padding:"10px 20px", borderRadius:8, border:`1px solid ${t.border.input}`, background:"none", color:t.text.secondary, cursor:"pointer", fontSize:13 }}>{k.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding:"10px 24px", borderRadius:8, border:"none", background:loading?t.bg.tag:"linear-gradient(135deg,#3b82f6,#6366f1)", color:"#fff", cursor:loading?"default":"pointer", fontSize:13, fontWeight:600, boxShadow:loading?"none":t.shadow.btn }}>
            {loading?(k.saving):isEdit?(k.update):(k.saveFivesAudit)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── KAİZEN FORM MODAL ────────────────────────────────────────────────────
const KaizenFormModal = ({ data, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const k = l.kaizenPage || {};
  const KAIZEN_CATEGORY = getKaizenCategory();
  const isEdit = !!data;
  const [form, setForm] = useState({ title:data?.title||"", category:data?.category||"efficiency", priority:data?.priority||"normal", department:data?.department||"", machine_id:data?.machine_id||"", problem:data?.problem||"", suggestion:data?.suggestion||"", estimated_saving:data?.estimated_saving||"", target_date:data?.target_date?.slice(0,10)||"", result:data?.result||"" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = (fk, v) => setForm(p => ({ ...p, [fk]:v }));

  const handleSave = async () => {
    if (!form.title||!form.problem) { setError(k.errTitleProblem); return; }
    setLoading(true); setError("");
    try {
      const url = isEdit ? `${API}/kaizen/${data.id}` : `${API}/kaizen`;
      const res = await fetch(url, { method:isEdit?"PUT":"POST", headers:headers(), body:JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail||(k.errGeneric));
      onSaved();
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const IS = { width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:640, maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"20px 28px", borderBottom:`1px solid ${t.border.default}`, display:"flex", justifyContent:"space-between", alignItems:"center", background:t.bg.header }}>
          <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>
            {isEdit?`✏️ ${k.editKaizen}`:`💡 ${k.newKaizen}`}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:t.text.muted, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:28 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {[
              { label:`${k.title_} *`, fk:"title", full:true },
              { label:k.category_,   fk:"category", opts:Object.entries(KAIZEN_CATEGORY).map(([v,c])=>({ v, l:`${c.icon} ${c.label}` })) },
              { label:k.priority_,    fk:"priority", opts:[{v:"low",l:k.prioLow},{v:"normal",l:k.prioNormal},{v:"high",l:k.prioHigh},{v:"critical",l:k.prioCritical}] },
              { label:k.department_,  fk:"department" },
              { label:k.machineArea, fk:"machine_id" },
              { label:k.targetDate_,  fk:"target_date", type:"date" },
              { label:k.estimatedSavingTl, fk:"estimated_saving", type:"number" },
            ].map(f=>(
              <div key={f.fk} style={{ gridColumn:f.full?"1 / -1":"auto" }}>
                <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{f.label}</label>
                {f.opts
                  ? <select value={form[f.fk]} onChange={e=>set(f.fk,e.target.value)} style={IS}>{f.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}</select>
                  : <input type={f.type||"text"} value={form[f.fk]} onChange={e=>set(f.fk,e.target.value)} style={IS} />}
              </div>
            ))}
          </div>
          {[
            { label:`${k.problemDef} *`,          fk:"problem",    ph:k.problemPh },
            { label:`${k.improvementSuggestion} *`, fk:"suggestion", ph:k.suggestionPh },
            { label:k.resultNote,          fk:"result",     ph:k.resultPh },
          ].map(f=>(
            <div key={f.fk} style={{ marginTop:14 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{f.label}</label>
              <textarea value={form[f.fk]} onChange={e=>set(f.fk,e.target.value)} placeholder={f.ph} rows={3} style={{ ...IS, resize:"vertical" }} />
            </div>
          ))}
          {error && <div style={{ marginTop:14, padding:"10px 16px", background:t.accent.redBg, border:`1px solid ${t.accent.red}44`, borderRadius:8, color:t.accent.red, fontSize:13 }}>{error}</div>}
        </div>
        <div style={{ padding:"16px 28px", borderTop:`1px solid ${t.border.default}`, display:"flex", justifyContent:"flex-end", gap:12, background:t.bg.header }}>
          <button onClick={onClose} style={{ padding:"10px 20px", borderRadius:8, border:`1px solid ${t.border.input}`, background:"none", color:t.text.secondary, cursor:"pointer", fontSize:13 }}>{k.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding:"10px 24px", borderRadius:8, border:"none", background:loading?t.bg.tag:"linear-gradient(135deg,#3b82f6,#6366f1)", color:"#fff", cursor:loading?"default":"pointer", fontSize:13, fontWeight:600, boxShadow:loading?"none":t.shadow.btn }}>
            {loading?(k.saving):isEdit?(k.update):(k.submit)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function Kaizen() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const k = useMemo(() => l.kaizenPage || {}, [l.kaizenPage]);

  const [mainTab,        setMainTab]        = useState(localStorage.getItem("tab_kaizen")||"kaizen");
  const [kaizenList,     setKaizenList]     = useState([]);
  const [fivesList,      setFivesList]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [,               setError]          = useState("");
  const [search,         setSearch]         = useState("");
  const [filterStatus,   setFilterStatus]   = useState("all");
  const [filterCat,      setFilterCat]      = useState("all");
  const [selectedKaizen, setSelectedKaizen] = useState(null);
  const [showKaizenForm, setShowKaizenForm] = useState(false);
  const [editingKaizen,  setEditingKaizen]  = useState(null);
  const [showFivesForm,  setShowFivesForm]  = useState(false);
  const [editingFives,   setEditingFives]   = useState(null);
  const [stats,          setStats]          = useState({});
  const [suggestions,    setSuggestions]    = useState([]);
  const [thresholds,     setThresholds]     = useState({ oee_threshold:70, fire_threshold:10, temp_threshold:80, energy_threshold:1000 });
  const [showThresholds, setShowThresholds] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/kaizen`, { headers:headers() }),
        fetch(`${API}/fives`,  { headers:headers() }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        const list = Array.isArray(data) ? data : data.items||[];
        setKaizenList(list);
        const completed   = list.filter(ki=>ki.status==="completed").length;
        const totalSaving = list.filter(ki=>ki.status==="completed").reduce((s,ki)=>s+(ki.estimated_saving||0),0);
        const pending     = list.filter(ki=>["idea","reviewing","approved","implementing"].includes(ki.status)).length;
        setStats(p=>({...p, total:list.length, completed, totalSaving, pending}));
      } else throw new Error(k.errFetch);
      if (r2.ok) {
        const data = await r2.json();
        const list = Array.isArray(data) ? data : data.items||[];
        setFivesList(list);
        const avgScore = list.length ? (list.reduce((s,f)=>s+((f.seiri+f.seiton+f.seiso+f.seiketsu+f.shitsuke)||0),0)/list.length).toFixed(1) : 0;
        setStats(p=>({...p, fivesTotal:list.length, avgFivesScore:avgScore}));
      }
      try {
        const r3 = await fetch(`${API}/kaizen/suggestions`, { headers:headers() });
        if (r3.ok) { const d = await r3.json(); setSuggestions(d.suggestions||[]); }
      } catch {}
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [k]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const KAIZEN_STATUS    = getKaizenStatus();
  const KAIZEN_CATEGORY  = getKaizenCategory();
  const FIVES_CATEGORIES = getFivesCategories();

  const filteredKaizen = kaizenList.filter(ki => {
    const ms = !search||(ki.title||"").toLowerCase().includes(search.toLowerCase());
    return ms && (filterStatus==="all"||ki.status===filterStatus) && (filterCat==="all"||ki.category===filterCat);
  });

  const lastAudit = fivesList[0];
  const radarData = FIVES_CATEGORIES.map(cat=>({ subject:cat.label.split(" ")[0], score:lastAudit?.[cat.key]||0, fullMark:10 }));
  const catData   = Object.entries(KAIZEN_CATEGORY).map(([key,cfg])=>({ name:`${cfg.icon} ${cfg.label}`, count:kaizenList.filter(ki=>ki.category===key).length })).filter(d=>d.count>0);

  const SS = { padding:"10px 14px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:10, color:t.text.primary, fontSize:13, outline:"none", cursor:"pointer" };

  return (
    <div style={{ minHeight:"100vh", background:t.bg.page, color:t.text.primary, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth:1400, margin:"0 auto", padding:"28px 24px" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
          <div>
            <h1 style={{ fontSize:26, fontWeight:800, color:t.text.primary, margin:0, letterSpacing:"-0.02em" }}>🔄 {k.title}</h1>
            <p style={{ color:t.text.muted, margin:"6px 0 0", fontSize:14 }}>{k.subtitle}</p>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={fetchData} style={{ padding:"10px 16px", borderRadius:10, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:13 }}>🔄 {k.refresh}</button>
            {mainTab==="kaizen" && <button onClick={()=>{setEditingKaizen(null);setShowKaizenForm(true);}} style={{ padding:"10px 20px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#3b82f6,#6366f1)", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:600, boxShadow:t.shadow.btn }}>💡 {k.addSuggestion}</button>}
            {mainTab==="fives"  && <button onClick={()=>{setEditingFives(null);setShowFivesForm(true);}} style={{ padding:"10px 20px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:600, boxShadow:"0 4px 14px rgba(16,185,129,0.3)" }}>📋 {k.doAudit}</button>}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:"flex", gap:12, marginBottom:24, flexWrap:"wrap" }}>
          {[
            { label:k.statTotal,    value:stats.total||0,   color:t.accent.blue,   icon:"💡" },
            { label:k.statCompleted,      value:stats.completed||0,color:t.accent.green,  icon:"✅" },
            { label:k.statOngoing,      value:stats.pending||0,  color:t.accent.yellow, icon:"⏳" },
            { label:k.statSaving,value:stats.totalSaving?`${(stats.totalSaving/1000).toFixed(0)}K ₺`:"0 ₺", color:t.accent.purple, icon:"💰" },
            { label:k.statFivesScore,   value:stats.avgFivesScore?`${stats.avgFivesScore}/50`:"—", color:t.accent.green, icon:"⭐" },
          ].map(s=>(
            <div key={s.label} style={{ flex:1, minWidth:130, background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:"14px 18px", display:"flex", alignItems:"center", gap:12, boxShadow:t.shadow.card }}>
              <span style={{ fontSize:22 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:t.text.muted, fontWeight:600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Sekmeler */}
        <div style={{ display:"flex", gap:4, marginBottom:20, background:t.bg.toggle, border:`1px solid ${t.border.input}`, borderRadius:12, padding:4, width:"fit-content" }}>
          {[
            ["kaizen",      `💡 ${k.tabKaizen}`],
            ["fives",       `5️⃣ ${k.tabFives}`],
            ["lean",        `🎯 ${k.tabLean}`],
            ["pdca",        `🔄 ${k.tabPdca}`],
            ["savings",     `💰 ${k.tabSavings}`],
            ["suggestions", `🤖 ${k.tabSuggestions}`],
          ].map(([v,label])=>(
            <button key={v} onClick={()=>{setMainTab(v);localStorage.setItem("tab_kaizen",v);setSearch("");setFilterStatus("all");}} style={{ padding:"9px 18px", borderRadius:9, border:"none", cursor:"pointer", background:mainTab===v?(themeName==="dark"?"rgba(255,255,255,0.12)":"#fff"):"none", color:mainTab===v?t.text.primary:t.text.muted, fontSize:13, fontWeight:600, boxShadow:mainTab===v?t.shadow.card:"none", transition:"all 0.2s" }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"80px 0", gap:12 }}>
            <div style={{ width:28, height:28, border:`2px solid ${t.border.default}`, borderTopColor:t.accent.blue, borderRadius:"50%", animation:"sf-spin 0.8s linear infinite" }} />
            <span style={{ color:t.text.muted }}>{k.loading}</span>
          </div>
        ) : (<>

          {/* SİSTEM ÖNERİLERİ */}
          {mainTab==="suggestions" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:13, color:t.text.muted }}>🤖 {k.sysInfo}</div>
                <button onClick={()=>setShowThresholds(p=>!p)} style={{ padding:"8px 14px", borderRadius:8, cursor:"pointer", fontSize:12, border:`1px solid ${t.accent.blueBorder}`, background:t.accent.blueBg, color:t.accent.blue, fontWeight:600 }}>⚙️ {k.thresholdSettings}</button>
              </div>
              {showThresholds && (
                <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:20, boxShadow:t.shadow.card }}>
                  <div style={{ fontSize:13, fontWeight:700, color:t.text.primary, marginBottom:16 }}>⚙️ {k.thresholdValues}</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 }}>
                    {[
                      { label:k.oeeThreshold,            key:"oee_threshold",    unit:"%" },
                      { label:k.fireThreshold,     key:"fire_threshold",   unit:"%" },
                      { label:k.tempThreshold,      key:"temp_threshold",   unit:"°C" },
                      { label:k.energyThreshold, key:"energy_threshold", unit:"₺" },
                    ].map(({label,key,unit})=>(
                      <div key={key}>
                        <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{label}</label>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <input type="number" value={thresholds[key]} onChange={e=>setThresholds(p=>({...p,[key]:Number(e.target.value)}))} style={{ flex:1, padding:"8px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none" }} />
                          <span style={{ fontSize:12, color:t.text.muted }}>{unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={fetchData} style={{ marginTop:14, padding:"9px 20px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#3b82f6,#6366f1)", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:600 }}>🔄 {k.reanalyze}</button>
                </div>
              )}
              {suggestions.length===0 ? (
                <div style={{ textAlign:"center", padding:"60px 0", background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, boxShadow:t.shadow.card }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
                  <div style={{ fontSize:15, fontWeight:700, color:t.text.primary, marginBottom:6 }}>{k.noSuggestions}</div>
                  <div style={{ fontSize:13, color:t.text.muted }}>{k.noSuggestionsNote}</div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  {suggestions.map((s,idx)=>{
                    const PC = { urgent:t.accent.red, high:t.accent.yellow, normal:t.accent.blue, low:t.text.muted };
                    const CI = { quality:"✅", efficiency:"⚡", safety:"🦺", cost:"💰", maintenance:"🔧", environment:"🌿" };
                    const pColor = PC[s.priority]||t.accent.blue;
                    const cIcon  = CI[s.category]||"📋";
                    return (
                      <div key={idx} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:20, boxShadow:t.shadow.card, position:"relative", overflow:"hidden" }}>
                        <div style={{ position:"absolute", top:0, left:0, bottom:0, width:4, background:pColor, borderRadius:"14px 0 0 14px" }} />
                        <div style={{ paddingLeft:12 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                              <span style={{ fontSize:22 }}>{cIcon}</span>
                              <div>
                                <div style={{ fontSize:14, fontWeight:700, color:t.text.primary }}>{s.title}</div>
                                <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{k.machine_}: {s.machine} · {k.category_}: {s.category}</div>
                              </div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, color:pColor, background:`${pColor}18`, border:`1px solid ${pColor}44`, textTransform:"uppercase" }}>{s.priority}</span>
                              {s.estimated_saving>0 && <span style={{ fontSize:12, fontWeight:700, color:t.accent.green }}>💰 {Number(s.estimated_saving).toLocaleString("tr-TR")} ₺ {k.saving_}</span>}
                            </div>
                          </div>
                          <div style={{ background:t.accent.redBg, border:`1px solid ${t.accent.red}22`, borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:t.accent.red, marginBottom:4, textTransform:"uppercase" }}>⚠ {k.problem_}</div>
                            <div style={{ fontSize:13, color:t.text.secondary }}>{s.problem}</div>
                          </div>
                          {s.suggestions&&s.suggestions.length>0 && (
                            <div style={{ background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:t.accent.blue, marginBottom:6, textTransform:"uppercase" }}>💡 {k.recommendedActions}</div>
                              {s.suggestions.map((tip,ti)=>(
                                <div key={ti} style={{ fontSize:12, color:t.text.secondary, padding:"3px 0", display:"flex", gap:6 }}><span style={{ color:t.accent.blue, flexShrink:0 }}>→</span>{tip}</div>
                              ))}
                            </div>
                          )}
                          <button onClick={async()=>{
                            try {
                              const res = await fetch(`${API}/kaizen`,{ method:"POST", headers:headers(), body:JSON.stringify({ title:s.title, problem:s.problem, suggestion:(s.suggestions||[]).join(", "), machine_id:s.machine, category:s.category, priority:s.priority, estimated_saving:s.estimated_saving||0 }) });
                              if (res.ok) { fetchData(); setMainTab("kaizen"); localStorage.setItem("tab_kaizen","kaizen"); }
                            } catch {}
                          }} style={{ padding:"9px 20px", borderRadius:8, border:"none", cursor:"pointer", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff", fontSize:13, fontWeight:600, boxShadow:"0 4px 12px rgba(16,185,129,0.3)" }}>
                            📋 {k.createKaizen}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* KAİZEN */}
          {mainTab==="kaizen" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
                <input placeholder={`🔍 ${k.searchPlaceholder}`} value={search} onChange={e=>setSearch(e.target.value)} style={{ ...SS, flex:1, minWidth:200 }} />
                <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={SS}>
                  <option value="all">{k.allStatuses}</option>
                  {Object.entries(KAIZEN_STATUS).map(([kk,v])=><option key={kk} value={kk}>{v.label}</option>)}
                </select>
                <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={SS}>
                  <option value="all">{k.allCategories}</option>
                  {Object.entries(KAIZEN_CATEGORY).map(([kk,v])=><option key={kk} value={kk}>{v.icon} {v.label}</option>)}
                </select>
              </div>
              {filteredKaizen.length===0 ? (
                <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>
                  {kaizenList.length===0?(k.noKaizen):(k.noMatch)}
                </div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:14 }}>
                  {filteredKaizen.map((ki,idx)=>{
                    const c = scKaizen(ki.status,themeName);
                    const catCfg = KAIZEN_CATEGORY[ki.category]||KAIZEN_CATEGORY.other;
                    return (
                      <div key={ki.id||idx} onClick={()=>setSelectedKaizen(ki)} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:18, cursor:"pointer", transition:"all 0.2s", boxShadow:t.shadow.card, position:"relative", overflow:"hidden" }}
                        onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor=`${c.color}55`;}}
                        onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.borderColor=t.border.card;}}>
                        <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${c.color},transparent)`, borderRadius:"14px 14px 0 0" }} />
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:20 }}>{catCfg.icon}</span>
                            <div>
                              <div style={{ fontSize:13, fontWeight:700, color:t.text.primary }}>{ki.title}</div>
                              <div style={{ fontSize:11, color:t.text.muted }}>{catCfg.label}</div>
                            </div>
                          </div>
                          <KaizenBadge status={ki.status} themeName={themeName} />
                        </div>
                        <div style={{ fontSize:12, color:t.text.secondary, marginBottom:12, lineHeight:1.5, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{ki.problem||"—"}</div>
                        <div style={{ display:"flex", justifyContent:"space-between", paddingTop:10, borderTop:`1px solid ${t.border.subtle}` }}>
                          <span style={{ fontSize:11, color:t.text.muted }}>👤 {ki.submitter_name||ki.created_by||"—"}</span>
                          {ki.estimated_saving && <span style={{ fontSize:11, color:t.accent.green, fontWeight:700 }}>💰 {Number(ki.estimated_saving).toLocaleString("tr-TR")} ₺</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 5S */}
          {mainTab==="fives" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
                <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:16 }}>
                    {k.lastAuditRadar}
                    {lastAudit && <span style={{ fontSize:12, color:t.text.muted, marginLeft:8 }}>— {lastAudit.area} · {fmt(lastAudit.audit_date)}</span>}
                  </div>
                  {lastAudit ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke={t.chart.grid} />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize:12, fill:t.chart.axis }} />
                        <Radar dataKey="score" stroke={t.accent.blue} fill={t.accent.blue} fillOpacity={0.3} />
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>{k.noAuditYet}</div>}
                </div>
                <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:16 }}>{k.fivesCategoryScores}</div>
                  {FIVES_CATEGORIES.map(cat=>{
                    const score = lastAudit?.[cat.key]||0;
                    const color = score>=8?t.accent.green:score>=5?t.accent.yellow:t.accent.red;
                    return (
                      <div key={cat.key} style={{ marginBottom:14 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:13, color:t.text.primary }}>{cat.icon} {cat.label.split("(")[0].trim()}</span>
                          <span style={{ fontSize:13, fontWeight:800, color }}>{score}/10</span>
                        </div>
                        <div style={{ height:8, background:t.bg.tag, borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${score*10}%`, background:color, borderRadius:4, transition:"width 0.6s ease" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
                <div style={{ padding:"16px 20px", borderBottom:`1px solid ${t.border.default}`, fontSize:14, fontWeight:700, color:t.text.primary }}>📋 {k.auditHistory}</div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr style={{ background:t.bg.theadRow }}>
                    {[k.date_,k.area_,k.auditor_,"Seiri","Seiton","Seiso","Seiketsu","Shitsuke",k.total_,k.actions].map(h=>(
                      <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {fivesList.length===0
                      ? <tr><td colSpan={10} style={{ textAlign:"center", color:t.text.placeholder, padding:40 }}>{k.noFivesAudits}</td></tr>
                      : fivesList.map((f,i)=>{
                        const total=(f.seiri||0)+(f.seiton||0)+(f.seiso||0)+(f.seiketsu||0)+(f.shitsuke||0);
                        const tc=total>=40?t.accent.green:total>=25?t.accent.yellow:t.accent.red;
                        return (
                          <tr key={f.id||i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                            <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>{fmt(f.audit_date)}</td>
                            <td style={{ padding:"11px 14px", fontWeight:600, color:t.text.primary, fontSize:13 }}>{f.area}</td>
                            <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>{f.auditor}</td>
                            {["seiri","seiton","seiso","seiketsu","shitsuke"].map(fk=>(
                              <td key={fk} style={{ padding:"11px 14px", textAlign:"center", fontWeight:700, fontSize:13, color:(f[fk]||0)>=8?t.accent.green:(f[fk]||0)>=5?t.accent.yellow:t.accent.red }}>{f[fk]||0}</td>
                            ))}
                            <td style={{ padding:"11px 14px", textAlign:"center", fontWeight:800, fontSize:14, color:tc }}>{total}/50</td>
                            <td style={{ padding:"11px 14px" }}>
                              <button onClick={()=>{setEditingFives(f);setShowFivesForm(true);}} style={{ padding:"4px 10px", borderRadius:6, fontSize:11, cursor:"pointer", background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, color:t.accent.blue }}>✏️</button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* LEAN */}
          {mainTab==="lean" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
                <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:16 }}>📊 {k.kaizenCategoryDist}</div>
                  {catData.length>0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={catData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} horizontal={false} />
                        <XAxis type="number" stroke={t.chart.axis} tick={{ fontSize:11, fill:t.chart.axis }} />
                        <YAxis type="category" dataKey="name" stroke={t.chart.axis} tick={{ fontSize:10, fill:t.chart.axis }} width={120} />
                        <Tooltip contentStyle={{ background:t.chart.tooltip.bg, border:`1px solid ${t.chart.tooltip.border}`, borderRadius:8, color:t.text.primary }} />
                        <Bar dataKey="count" fill={t.accent.blue} radius={[0,4,4,0]} name={k.count_} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>{k.noData}</div>}
                </div>
                <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                  <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:16 }}>🎯 {k.sevenMuda}</div>
                  {[
                    { label:k.mudaOverprod,    icon:"📦", desc:k.mudaOverprodDesc },
                    { label:k.mudaWaiting,          icon:"⏳", desc:k.mudaWaitingDesc },
                    { label:k.mudaTransport,  icon:"🚚", desc:k.mudaTransportDesc },
                    { label:k.mudaOverproc,     icon:"⚙️", desc:k.mudaOverprocDesc },
                    { label:k.mudaInventory,     icon:"🗃️", desc:k.mudaInventoryDesc },
                    { label:k.mudaMotion, icon:"🚶", desc:k.mudaMotionDesc },
                    { label:k.mudaDefect,icon:"🔄", desc:k.mudaDefectDesc },
                  ].map((muda,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 0", borderBottom:`1px solid ${t.border.subtle}` }}>
                      <span style={{ fontSize:18, width:24 }}>{muda.icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:t.text.primary }}>{muda.label}</div>
                        <div style={{ fontSize:11, color:t.text.muted }}>{muda.desc}</div>
                      </div>
                      <span style={{ fontSize:11, color:t.accent.blue, fontWeight:600 }}>
                        {kaizenList.filter(ki=>ki.lean_type===muda.label||ki.category==="efficiency").length} {k.suggestion__}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
                {[
                  { label:k.totalSuggestions_,    value:kaizenList.length,                                                                                   unit:k.pcs,  color:t.accent.blue },
                  { label:k.completedCount,      value:kaizenList.filter(ki=>ki.status==="completed").length,                                                unit:k.pcs,  color:t.accent.green },
                  { label:k.completionRate,value:kaizenList.length?((kaizenList.filter(ki=>ki.status==="completed").length/kaizenList.length)*100).toFixed(0):0, unit:"%", color:t.accent.yellow },
                  { label:k.totalSaving_, value:(stats.totalSaving/1000||0).toFixed(0),                                                              unit:"K ₺",           color:t.accent.purple },
                  { label:k.fivesAuditCount,value:fivesList.length,                                                                                  unit:k.pcs,  color:t.accent.blue },
                  { label:k.avgFivesScore_,  value:`${stats.avgFivesScore||0}`,                                                                         unit:"/ 50",          color:Number(stats.avgFivesScore)>=40?t.accent.green:t.accent.yellow },
                ].map(({ label,value,unit,color })=>(
                  <div key={label} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:12, padding:"18px 20px", boxShadow:t.shadow.card }}>
                    <div style={{ fontSize:11, color:t.text.muted, marginBottom:6, textTransform:"uppercase", fontWeight:600 }}>{label}</div>
                    <div style={{ fontSize:26, fontWeight:800, color }}>{value}<span style={{ fontSize:13, color:t.text.muted, marginLeft:4 }}>{unit}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PDCA */}
          {mainTab==="pdca" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <div style={{ padding:"12px 18px", background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, borderRadius:10, fontSize:13, color:t.accent.blue }}>
                🔄 {k.pdcaInfo}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                {[
                  { label:"Plan",  icon:"📋", color:t.accent.blue,   desc:k.pdcaPlanDesc, statuses:["idea","reviewing"] },
                  { label:"Do",    icon:"⚙️", color:t.accent.yellow, desc:k.pdcaDoDesc,   statuses:["approved","implementing"] },
                  { label:"Check", icon:"🔍", color:t.accent.purple, desc:k.pdcaCheckDesc,         statuses:["implementing"] },
                  { label:"Act",   icon:"✅", color:t.accent.green,  desc:k.pdcaActDesc,     statuses:["completed"] },
                ].map(({ label,icon,color,desc,statuses })=>{
                  const count = kaizenList.filter(ki=>statuses.includes(ki.status)).length;
                  return (
                    <div key={label} style={{ background:t.bg.card, border:`2px solid ${color}44`, borderRadius:16, padding:20, boxShadow:t.shadow.card, position:"relative", overflow:"hidden" }}>
                      <div style={{ position:"absolute", top:0, left:0, right:0, height:4, background:color, borderRadius:"16px 16px 0 0" }} />
                      <div style={{ fontSize:28, marginBottom:8 }}>{icon}</div>
                      <div style={{ fontSize:18, fontWeight:800, color, marginBottom:6 }}>{label}</div>
                      <div style={{ fontSize:12, color:t.text.muted, marginBottom:14, lineHeight:1.5 }}>{desc}</div>
                      <div style={{ fontSize:28, fontWeight:800, color }}>{count}</div>
                      <div style={{ fontSize:11, color:t.text.muted }}>{k.suggestionInStage}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
                <div style={{ padding:"16px 20px", borderBottom:`1px solid ${t.border.default}`, fontSize:14, fontWeight:700, color:t.text.primary }}>{k.activePdca}</div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr style={{ background:t.bg.theadRow }}>
                    {[k.kaizen_,k.category_,k.pdcaPhase,k.targetDate_,k.estimatedSaving_,k.responsible].map(h=>(
                      <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {kaizenList.filter(ki=>ki.status!=="rejected"&&ki.status!=="completed").length===0
                      ? <tr><td colSpan={6} style={{ textAlign:"center", color:t.text.placeholder, padding:40 }}>{k.noActivePdca}</td></tr>
                      : kaizenList.filter(ki=>ki.status!=="rejected"&&ki.status!=="completed").map((ki,i)=>{
                        const pp = { idea:{label:"📋 Plan",color:t.accent.blue}, reviewing:{label:"📋 Plan",color:t.accent.blue}, approved:{label:"⚙️ Do",color:t.accent.yellow}, implementing:{label:"🔍 Check",color:t.accent.purple} }[ki.status]||{label:"—",color:t.text.muted};
                        const catCfg = KAIZEN_CATEGORY[ki.category]||KAIZEN_CATEGORY.other;
                        return (
                          <tr key={ki.id||i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                            <td style={{ padding:"11px 14px", fontWeight:600, color:t.text.primary, fontSize:13 }}>{ki.title}</td>
                            <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>{catCfg.icon} {catCfg.label}</td>
                            <td style={{ padding:"11px 14px" }}><span style={{ fontSize:12, fontWeight:700, color:pp.color }}>{pp.label}</span></td>
                            <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>{ki.target_date?new Date(ki.target_date).toLocaleDateString(getLocale()):"—"}</td>
                            <td style={{ padding:"11px 14px", fontWeight:600, color:t.accent.green, fontSize:13 }}>{ki.estimated_saving?`${Number(ki.estimated_saving).toLocaleString("tr-TR")} ₺`:"—"}</td>
                            <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>{ki.submitter_name||ki.created_by||"—"}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MALİYET-FAYDA */}
          {mainTab==="savings" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                {[
                  { label:k.totalEstSaving, value:money(stats.totalSaving||0), color:t.accent.green, icon:"💰" },
                  { label:k.completedKaizen,       value:kaizenList.filter(ki=>ki.status==="completed").length, color:t.accent.blue, icon:"✅" },
                  { label:k.avgSavingPerK,   value:kaizenList.filter(ki=>ki.status==="completed").length>0?money(Math.round((stats.totalSaving||0)/kaizenList.filter(ki=>ki.status==="completed").length)):"—", color:t.accent.purple, icon:"📊" },
                  { label:k.completionRate,        value:kaizenList.length>0?`${((kaizenList.filter(ki=>ki.status==="completed").length/kaizenList.length)*100).toFixed(0)}%`:"0%", color:t.accent.yellow, icon:"📈" },
                ].map(s=>(
                  <div key={s.label} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:"16px 18px", boxShadow:t.shadow.card, display:"flex", alignItems:"center", gap:12 }}>
                    <span style={{ fontSize:22 }}>{s.icon}</span>
                    <div>
                      <div style={{ fontSize:18, fontWeight:800, color:s.color }}>{s.value}</div>
                      <div style={{ fontSize:11, color:t.text.muted, fontWeight:600 }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:20 }}>💰 {k.categorySavingAnalysis}</div>
                {Object.entries(KAIZEN_CATEGORY).map(([key,cfg])=>{
                  const catItems       = kaizenList.filter(ki=>ki.category===key);
                  const completed      = catItems.filter(ki=>ki.status==="completed");
                  const totalSaving    = catItems.reduce((s,ki)=>s+(ki.estimated_saving||0),0);
                  const realizedSaving = completed.reduce((s,ki)=>s+(ki.estimated_saving||0),0);
                  if (catItems.length===0) return null;
                  const pct = totalSaving>0?(realizedSaving/totalSaving*100):0;
                  return (
                    <div key={key} style={{ marginBottom:16 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:16 }}>{cfg.icon}</span>
                          <span style={{ fontSize:13, fontWeight:600, color:t.text.primary }}>{cfg.label}</span>
                          <span style={{ fontSize:11, color:t.text.muted }}>({catItems.length} {k.suggestion__})</span>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <span style={{ fontSize:13, fontWeight:700, color:t.accent.green }}>{money(realizedSaving)}</span>
                          <span style={{ fontSize:11, color:t.text.muted }}> / {money(totalSaving)}</span>
                        </div>
                      </div>
                      <div style={{ height:8, background:t.bg.tag, borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${t.accent.green}88,${t.accent.green})`, borderRadius:4, transition:"width 0.6s ease" }} />
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                        <span style={{ fontSize:10, color:t.text.muted }}>{k.realized}: {pct.toFixed(0)}%</span>
                        <span style={{ fontSize:10, color:t.text.muted }}>{completed.length}/{catItems.length} {k.completed_}</span>
                      </div>
                    </div>
                  );
                })}
                {kaizenList.length===0 && <div style={{ textAlign:"center", color:t.text.placeholder, padding:"40px 0" }}>{k.noKaizenData}</div>}
              </div>
              <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
                <div style={{ padding:"16px 20px", borderBottom:`1px solid ${t.border.default}`, fontSize:14, fontWeight:700, color:t.text.primary }}>📊 {k.roiAnalysis}</div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr style={{ background:t.bg.theadRow }}>
                    {[k.kaizen_,k.category_,k.implCost,k.estimatedSaving_,"ROI",k.payback].map(h=>(
                      <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {kaizenList.filter(ki=>ki.status==="completed").length===0
                      ? <tr><td colSpan={6} style={{ textAlign:"center", color:t.text.placeholder, padding:40 }}>{k.noCompletedKaizen}</td></tr>
                      : kaizenList.filter(ki=>ki.status==="completed").map((ki,i)=>{
                        const cost=ki.implementation_cost||0, saving=ki.estimated_saving||0;
                        const roi=cost>0?(((saving-cost)/cost)*100).toFixed(0):"∞";
                        const payback=saving>0?(cost/(saving/12)).toFixed(1):"—";
                        const catCfg=KAIZEN_CATEGORY[ki.category]||KAIZEN_CATEGORY.other;
                        return (
                          <tr key={ki.id||i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                            <td style={{ padding:"11px 14px", fontWeight:600, color:t.text.primary, fontSize:13 }}>{ki.title}</td>
                            <td style={{ padding:"11px 14px", fontSize:12, color:t.text.muted }}>{catCfg.icon} {catCfg.label}</td>
                            <td style={{ padding:"11px 14px", fontSize:13, color:t.accent.red }}>{money(cost)}</td>
                            <td style={{ padding:"11px 14px", fontSize:13, color:t.accent.green, fontWeight:600 }}>{money(saving)}</td>
                            <td style={{ padding:"11px 14px", fontWeight:800, fontSize:14, color:roi==="∞"||Number(roi)>0?t.accent.green:t.accent.red }}>{roi}%</td>
                            <td style={{ padding:"11px 14px", fontSize:13, color:t.accent.blue }}>{payback!=="—"?`${payback} ${k.monthShort}`:"—"}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>)}
      </div>

      {selectedKaizen && <KaizenDetailModal record={selectedKaizen} t={t} themeName={themeName} onClose={()=>setSelectedKaizen(null)} onEdit={(ki)=>{setEditingKaizen(ki);setShowKaizenForm(true);setSelectedKaizen(null);}} onStatusChange={()=>{fetchData();setSelectedKaizen(null);}} />}
      {showKaizenForm && <KaizenFormModal data={editingKaizen} t={t} onClose={()=>{setShowKaizenForm(false);setEditingKaizen(null);}} onSaved={()=>{setShowKaizenForm(false);setEditingKaizen(null);fetchData();}} />}
      {showFivesForm  && <FiveSAuditModal audit={editingFives} t={t} onClose={()=>{setShowFivesForm(false);setEditingFives(null);}} onSaved={()=>{setShowFivesForm(false);setEditingFives(null);fetchData();}} />}
    </div>
  );
}
